import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { RoleBadge, StatusBadge } from '@/components/club/member-badges';
import { MembershipRequestsTab } from '@/components/club/membership-requests-tab';
import { clubSidebar } from '@/config/sidebarConfig.ts';
import { getMembershipRequestsApi, removeMemberApi, updateMembershipRequestApi } from '@/lib/api/clubsApi';
import { getUsersApi } from '@/lib/api/usersApi';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, getInitials } from '@shared';
import { ClubMembershipRequestStatus } from '@shared/enums/domain.enum';
import { Role } from '@shared/enums/role.enum';
import type { AuthUser } from '@shared/types/entities';
import type { ClubMembershipRequest } from '@shared/types/entities';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Mail, Phone, Search, UserMinus, Users } from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import ConfirmModal from '../../components/ui/ConfirmModal';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';

const PAGE_SIZE = 15;
const REQ_PAGE_SIZE = 20;

// ─── Page ────────────────────────────────────────────────────────

export default function ClubMembers() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const clubId = user?.clubId ?? undefined;
  const isAdmin = user?.role === Role.CLUB_ADMIN || user?.role === Role.SUPER_ADMIN;
  const queryClient = useQueryClient();

  // ── URL params
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') ?? 'membres') as 'membres' | 'demandes';
  const page = parseInt(searchParams.get('page') ?? '1', 10);
  const disabledFilter = (searchParams.get('disabled') ?? '') as '' | 'true' | 'false';
  const reqPage = parseInt(searchParams.get('reqPage') ?? '1', 10);
  const reqStatusFilter = (searchParams.get('reqStatus') ?? '') as ClubMembershipRequestStatus | '';
  const debouncedSearch = searchParams.get('search') ?? '';

  const setParam = (key: string, value: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  // ── Local search state with debounce
  const [searchInput, setSearchInput] = useState(debouncedSearch);
  const [memberToRemove, setMemberToRemove] = useState<AuthUser | null>(null);

  // ── Demandes tab state
  const [reqToProcess, setReqToProcess] = useState<{
    request: ClubMembershipRequest;
    action: 'accept' | 'reject';
  } | null>(null);

  // ─── Queries ────────────────────────────────────────────────

  const membersParams = {
    page,
    limit: PAGE_SIZE,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(clubId && { clubId }),
    ...(disabledFilter !== '' && { disabled: disabledFilter === 'true' }),
  };

  const {
    data: membersData,
    isLoading: membersLoading,
    isFetching: membersFetching,
    isError: membersError,
    refetch: membersRefetch,
  } = useQuery({
    queryKey: queryKeys.users.list(membersParams),
    queryFn: () => getUsersApi(membersParams),
    placeholderData: keepPreviousData,
    enabled: isAuthenticated,
  });

  const reqParams = {
    page: reqPage,
    limit: REQ_PAGE_SIZE,
    ...(reqStatusFilter && { status: reqStatusFilter }),
  };

  const {
    data: reqData,
    isLoading: reqLoading,
    isFetching: reqFetching,
  } = useQuery({
    queryKey: queryKeys.clubs.membershipRequests(clubId ?? 0, reqParams as Record<string, unknown>),
    queryFn: () => getMembershipRequestsApi(clubId!, reqParams),
    enabled: !!clubId && isAdmin && isAuthenticated,
    placeholderData: keepPreviousData,
  });

  // Count of pending requests for the badge
  const { data: pendingCountData } = useQuery({
    queryKey: queryKeys.clubs.membershipRequests(clubId ?? 0, {
      status: ClubMembershipRequestStatus.PENDING,
      limit: 1,
    }),
    queryFn: () => getMembershipRequestsApi(clubId!, { status: ClubMembershipRequestStatus.PENDING, limit: 1 }),
    enabled: !!clubId && isAdmin && isAuthenticated,
  });
  const pendingCount = pendingCountData?.meta?.total ?? 0;

  // ─── Mutations ───────────────────────────────────────────────

  const removeMemberMutation = useMutation({
    mutationFn: (userId: number) => removeMemberApi(clubId!, userId),
    onSuccess: () => {
      toast.success('Membre retiré du club');
      setMemberToRemove(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? 'Erreur lors de la suppression');
      setMemberToRemove(null);
    },
  });

  const updateRequestMutation = useMutation({
    mutationFn: ({
      requestId,
      status,
    }: {
      requestId: number;
      status: ClubMembershipRequestStatus.ACCEPTED | ClubMembershipRequestStatus.REJECTED;
    }) => updateMembershipRequestApi(clubId!, requestId, { status }),
    onSuccess: (_, vars) => {
      const label = vars.status === ClubMembershipRequestStatus.ACCEPTED ? 'acceptée' : 'refusée';
      toast.success(`Demande ${label}`);
      setReqToProcess(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? 'Erreur lors du traitement');
      setReqToProcess(null);
    },
  });

  // ─── Handlers ────────────────────────────────────────────────

  function handleSearchChange(value: string) {
    setSearchInput(value);
  }

  // Debounce search → URL
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set('search', searchInput);
          else next.delete('search');
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // ─── Derived ─────────────────────────────────────────────────

  const members = membersData?.data ?? [];
  const totalMembers = membersData?.meta?.total ?? 0;
  const totalMemberPages = membersData?.meta?.totalPages ?? 1;
  const fromM = totalMembers === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toM = Math.min(page * PAGE_SIZE, totalMembers);

  const requests: ClubMembershipRequest[] = reqData?.data ?? [];
  const totalReqs = reqData?.meta?.total ?? 0;
  const totalReqPages = reqData?.meta?.totalPages ?? 1;
  const fromR = totalReqs === 0 ? 0 : (reqPage - 1) * REQ_PAGE_SIZE + 1;
  const toR = Math.min(reqPage * REQ_PAGE_SIZE, totalReqs);

  // ─── Render ──────────────────────────────────────────────────

  if (membersError) {
    return (
      <DashboardLayout sidebarConfig={clubSidebar}>
        <div className='p-8'>
          <QueryErrorState onRetry={membersRefetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={clubSidebar}>
      <div className='p-8'>
        <PageHeader
          title='Membres du club'
          subtitle='Gérez les adhérents de votre club'
          actions={
            <div className='flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-2'>
              <Users className='h-5 w-5 text-emerald-600' />
              <span className='text-sm font-bold text-emerald-700'>{membersLoading ? '…' : totalMembers} membres</span>
            </div>
          }
        />

        {/* Tabs */}
        <div className='mb-6 border-b border-slate-200'>
          <nav className='-mb-px flex gap-6'>
            <button
              onClick={() => setParam('tab', '')}
              className={`pb-3 text-sm font-semibold transition-colors border-b-2 ${
                activeTab === 'membres'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Membres
            </button>
            {isAdmin && (
              <button
                onClick={() => setParam('tab', 'demandes')}
                className={`pb-3 text-sm font-semibold transition-colors border-b-2 flex items-center gap-2 ${
                  activeTab === 'demandes'
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Demandes d'adhésion
                {pendingCount > 0 && (
                  <span className='min-w-5 h-5 rounded-full bg-amber-500 px-1.5 text-xs font-bold text-white flex items-center justify-center'>
                    {pendingCount}
                  </span>
                )}
              </button>
            )}
          </nav>
        </div>

        {/* ── Tab: Membres ─────────────────────────────────── */}
        {activeTab === 'membres' && (
          <>
            <div className='mb-6 flex items-center gap-4'>
              <div className='relative max-w-md flex-1'>
                <Search className='absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400' />
                <input
                  type='text'
                  value={searchInput}
                  onChange={e => handleSearchChange(e.target.value)}
                  placeholder='Rechercher par nom, email…'
                  className='w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 focus:border-emerald-500 focus:outline-none'
                />
              </div>
              <select
                value={disabledFilter}
                onChange={e => {
                  setSearchParams(
                    prev => {
                      const next = new URLSearchParams(prev);
                      if (e.target.value) next.set('disabled', e.target.value);
                      else next.delete('disabled');
                      next.set('page', '1');
                      return next;
                    },
                    { replace: true },
                  );
                }}
                className='rounded-lg border border-slate-200 px-4 py-2.5 focus:border-emerald-500 focus:outline-none'
              >
                <option value=''>Tous les statuts</option>
                <option value='false'>Actifs</option>
                <option value='true'>Inactifs</option>
              </select>
              {membersFetching && !membersLoading && <span className='text-xs text-slate-400'>Actualisation…</span>}
            </div>

            <div className='rounded-xl border border-slate-200 bg-white'>
              <div className='overflow-x-auto'>
                <table className='w-full'>
                  <thead className='border-b border-slate-200 bg-slate-50'>
                    <tr>
                      <th
                        scope='col'
                        className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                      >
                        Membre
                      </th>
                      <th
                        scope='col'
                        className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                      >
                        Contact
                      </th>
                      <th
                        scope='col'
                        className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                      >
                        Rôle
                      </th>
                      <th
                        scope='col'
                        className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                      >
                        Statut
                      </th>
                      <th
                        scope='col'
                        className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                      >
                        Licence
                      </th>
                      <th
                        scope='col'
                        className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                      >
                        Inscrit le
                      </th>
                      {isAdmin && (
                        <th
                          scope='col'
                          className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500'
                        >
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-slate-100'>
                    {membersLoading ? (
                      Array.from({ length: 8 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: isAdmin ? 7 : 6 }).map((_, j) => (
                            <td key={j} className='px-6 py-4'>
                              <div className='h-4 animate-pulse rounded bg-slate-200' />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : members.length === 0 ? (
                      <tr>
                        <td colSpan={isAdmin ? 7 : 6} className='px-6 py-12 text-center text-sm text-slate-500'>
                          Aucun membre trouvé
                        </td>
                      </tr>
                    ) : (
                      members.map(m => (
                        <tr key={m.id} className={`transition hover:bg-slate-50 ${m.disabled ? 'opacity-60' : ''}`}>
                          <td className='px-6 py-4'>
                            <div className='flex items-center gap-3'>
                              <div className='flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-xs font-bold text-white'>
                                {getInitials(m.firstName, m.lastName)}
                              </div>
                              <div>
                                <p className='text-sm font-semibold text-slate-900'>
                                  {m.firstName} {m.lastName}
                                </p>
                                <p className='text-xs text-slate-500'>@{m.username}</p>
                              </div>
                            </div>
                          </td>
                          <td className='px-6 py-4'>
                            <div className='flex flex-col gap-0.5'>
                              <span className='flex items-center gap-1.5 text-sm text-slate-700'>
                                <Mail className='h-3.5 w-3.5 text-slate-400' />
                                {m.email}
                              </span>
                              {m.mobile && (
                                <span className='flex items-center gap-1.5 text-xs text-slate-500'>
                                  <Phone className='h-3.5 w-3.5 text-slate-400' />
                                  {m.mobile}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className='px-6 py-4'>
                            <RoleBadge role={m.role} isClubAdmin={m.isClubAdmin} />
                          </td>
                          <td className='px-6 py-4'>
                            <StatusBadge disabled={m.disabled} />
                          </td>
                          <td className='px-6 py-4'>
                            <span className='text-sm text-slate-700'>{m.licenseNumber ?? '—'}</span>
                          </td>
                          <td className='px-6 py-4'>
                            <span className='text-sm text-slate-700'>{formatDateAbbreviatedFr(m.createdAt)}</span>
                          </td>
                          {isAdmin && (
                            <td className='px-6 py-4'>
                              {m.id !== user?.id && (
                                <button
                                  onClick={() => setMemberToRemove(m)}
                                  className='flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors cursor-pointer'
                                  title='Retirer du club'
                                >
                                  <UserMinus className='h-3.5 w-3.5' />
                                  Retirer
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination membres */}
              <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
                <p className='text-sm text-slate-500'>
                  {totalMembers === 0
                    ? 'Aucun résultat'
                    : `${fromM}–${toM} sur ${totalMembers.toLocaleString('fr-FR')} membres`}
                </p>
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => setParam('page', String(Math.max(1, page - 1)))}
                    disabled={page === 1}
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                    aria-label='Page précédente'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>
                  {getPageNumbers(page, totalMemberPages).map((p, i) =>
                    p === '...' ? (
                      <span key={`d-${i}`} className='px-2 text-slate-400'>
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setParam('page', String(p))}
                        aria-current={p === page ? 'page' : undefined}
                        className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                          p === page ? 'bg-emerald-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() => setParam('page', String(Math.min(totalMemberPages, page + 1)))}
                    disabled={page >= totalMemberPages}
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                    aria-label='Page suivante'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Tab: Demandes ────────────────────────────────── */}
        {activeTab === 'demandes' && isAdmin && (
          <MembershipRequestsTab
            requests={requests}
            reqLoading={reqLoading}
            reqFetching={reqFetching}
            reqStatusFilter={reqStatusFilter}
            onReqStatusFilterChange={value => {
              setSearchParams(
                prev => {
                  const next = new URLSearchParams(prev);
                  if (value) next.set('reqStatus', value);
                  else next.delete('reqStatus');
                  next.set('reqPage', '1');
                  return next;
                },
                { replace: true },
              );
            }}
            totalReqs={totalReqs}
            fromR={fromR}
            toR={toR}
            reqPage={reqPage}
            totalReqPages={totalReqPages}
            onPageChange={p => setParam('reqPage', p)}
            onProcess={(req, action) => setReqToProcess({ request: req, action })}
          />
        )}
      </div>

      {/* ── Confirm: retirer un membre ── */}
      <ConfirmModal
        isOpen={!!memberToRemove}
        title='Retirer ce membre'
        message={`Voulez-vous retirer ${memberToRemove?.firstName ?? ''} ${memberToRemove?.lastName ?? ''} du club ? Cette action peut être annulée en réacceptant une nouvelle demande.`}
        confirmLabel='Retirer'
        isLoading={removeMemberMutation.isPending}
        onConfirm={() => memberToRemove && removeMemberMutation.mutate(memberToRemove.id)}
        onCancel={() => setMemberToRemove(null)}
      />

      {/* ── Confirm: accepter une demande ── */}
      <ConfirmModal
        isOpen={reqToProcess?.action === 'accept'}
        variant='primary'
        title='Accepter la demande'
        message={`Accepter la demande de ${reqToProcess?.request.user?.firstName ?? ''} ${reqToProcess?.request.user?.lastName ?? ''} ? Le joueur sera immédiatement ajouté comme membre du club.`}
        confirmLabel='Accepter'
        isLoading={updateRequestMutation.isPending}
        onConfirm={() =>
          reqToProcess &&
          updateRequestMutation.mutate({
            requestId: reqToProcess.request.id,
            status: ClubMembershipRequestStatus.ACCEPTED,
          })
        }
        onCancel={() => setReqToProcess(null)}
      />

      {/* ── Confirm: refuser une demande ── */}
      <ConfirmModal
        isOpen={reqToProcess?.action === 'reject'}
        title='Refuser la demande'
        message={`Refuser la demande de ${reqToProcess?.request.user?.firstName ?? ''} ${reqToProcess?.request.user?.lastName ?? ''} ?`}
        confirmLabel='Refuser'
        isLoading={updateRequestMutation.isPending}
        onConfirm={() =>
          reqToProcess &&
          updateRequestMutation.mutate({
            requestId: reqToProcess.request.id,
            status: ClubMembershipRequestStatus.REJECTED,
          })
        }
        onCancel={() => setReqToProcess(null)}
      />
    </DashboardLayout>
  );
}
